import {
  BarChart3,
  Shield,
  Users,
  CheckCircle2,
  Tag,
  BookOpen,
  Scale,
  MessageSquare,
  Heart,
} from 'lucide-react';
import type { MetadataPillarDetails } from '../../../types/analysis.types';
import { MetricCard, SectionHeader, DistributionBar, RiskImplicationsPanel } from './shared';
import { safeNum, type PillarDetailsCommonProps } from './shared.utils';

interface Props extends PillarDetailsCommonProps {
  details: MetadataPillarDetails;
}

export const MetadataDetails: React.FC<Props> = ({
  details,
  metricBg,
  trackColor,
  theme,
  score,
}) => {
  const d = details as unknown as Record<string, unknown>;
  const hasReadme = Boolean(d.hasReadme ?? d.has_readme);
  const hasLicense = Boolean(d.hasLicense ?? d.has_license);
  const hasDescription = Boolean(d.hasDescription ?? d.has_description);
  const hasTopics = Boolean(d.hasTopics ?? d.has_topics);
  const topicCount = safeNum(d.topicCount ?? d.topic_count) ?? 0;
  const hasContributing = Boolean(d.hasContributing ?? d.has_contributing);
  const hasCodeOfConduct = Boolean(d.hasCodeOfConduct ?? d.has_code_of_conduct);

  const metadataItems = [
    {
      label: 'README',
      present: hasReadme,
      description: 'Project documentation',
      icon: <BookOpen className="w-4 h-4" />,
      importance: 'critical',
    },
    {
      label: 'License',
      present: hasLicense,
      description: 'Usage terms defined',
      icon: <Scale className="w-4 h-4" />,
      importance: 'critical',
    },
    {
      label: 'Description',
      present: hasDescription,
      description: 'Project summary',
      icon: <MessageSquare className="w-4 h-4" />,
      importance: 'high',
    },
    {
      label: 'Topics',
      present: hasTopics,
      description: 'Discoverability tags',
      icon: <Tag className="w-4 h-4" />,
      importance: 'medium',
    },
    {
      label: 'Contributing Guide',
      present: hasContributing,
      description: 'Contributor instructions',
      icon: <Users className="w-4 h-4" />,
      importance: 'medium',
    },
    {
      label: 'Code of Conduct',
      present: hasCodeOfConduct,
      description: 'Community guidelines',
      icon: <Heart className="w-4 h-4" />,
      importance: 'low',
    },
  ];

  const completedCount = metadataItems.filter((i) => i.present).length;

  const getRiskMessage = () => {
    if (!hasLicense)
      return 'Missing license makes it legally unclear how others can use this code, limiting adoption.';
    if (!hasReadme)
      return 'No README makes it difficult for new users and contributors to understand the project.';
    if (!hasDescription)
      return 'Missing description reduces discoverability and first impressions.';
    return null;
  };

  const riskMessage = getRiskMessage();

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <MetricCard
          label="Completeness"
          value={`${Math.round((completedCount / metadataItems.length) * 100)}%`}
          subtext={`${completedCount}/${metadataItems.length} items`}
          icon={<CheckCircle2 className="w-4 h-4 text-secondary" />}
          metricBg={metricBg}
        />
        {topicCount > 0 && (
          <MetricCard
            label="Topics"
            value={topicCount}
            icon={<Tag className="w-4 h-4 text-secondary" />}
            metricBg={metricBg}
          />
        )}
      </div>

      <div>
        <SectionHeader
          icon={<BarChart3 className="w-4 h-4" />}
          title="Documentation Distribution"
        />
        <div className="space-y-2">
          {metadataItems.map((item) => (
            <DistributionBar
              key={item.label}
              label={item.label}
              value={item.present ? 1 : 0}
              total={1}
              trackColor={trackColor}
              barColor={item.present ? '#10b981' : trackColor}
            />
          ))}
        </div>
      </div>

      {riskMessage && score < 70 && (
        <div>
          <SectionHeader icon={<Shield className="w-4 h-4" />} title="Risk Implications" />
          <RiskImplicationsPanel message={riskMessage} theme={theme} />
        </div>
      )}
    </div>
  );
};
